import { useState } from "react";
import AuditTool from "./AuditTool";

/* ── GLOBAL STYLES ─────────────────────────────────────────── */
const GS = `
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=Exo+2:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  ::-webkit-scrollbar { width: 3px; height: 3px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(38,193,110,0.3); border-radius: 2px; }
  @keyframes fadeUp   { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn   { from{opacity:0} to{opacity:1} }
  @keyframes pulse    { 0%,100%{opacity:1} 50%{opacity:0.35} }
  @keyframes spin     { to{transform:rotate(360deg)} }
  @keyframes blink    { 50%{opacity:0} }
  @keyframes slideIn  { from{transform:translateX(-10px);opacity:0} to{transform:translateX(0);opacity:1} }
  @keyframes borderGlow { 0%,100%{box-shadow:0 0 10px rgba(38,193,110,0.25)} 50%{box-shadow:0 0 22px rgba(56,139,248,0.35)} }
  .fu  { animation: fadeUp  0.4s ease forwards; }
  .fi  { animation: fadeIn  0.35s ease forwards; }
  .si  { animation: slideIn 0.35s ease forwards; }
  .pulse { animation: pulse 2s ease infinite; }
  .spin  { animation: spin  1s linear infinite; }
  .cursor{ animation: blink 0.9s step-end infinite; }
  .brand-glow { animation: borderGlow 3s ease infinite; }
`;

/* ── DESIGN TOKENS ─────────────────────────────────────────── */
const C = {
  bg:       "#060A0E",
  bg2:      "#0A1018",
  surface:  "rgba(255,255,255,0.03)",
  border:   "rgba(255,255,255,0.07)",
  borderHi: "rgba(38,193,110,0.28)",
  grad:     "linear-gradient(135deg, #26C16E, #388BF8)",
  accent:   "#26C16E",
  accentD:  "rgba(38,193,110,0.1)",
  accentB:  "rgba(38,193,110,0.22)",
  blue:     "#388BF8",
  blueD:    "rgba(56,139,248,0.1)",
  blueB:    "rgba(56,139,248,0.22)",
  purple:   "#A78BFA",
  purpleD:  "rgba(167,139,250,0.1)",
  orange:   "#FB923C",
  orangeD:  "rgba(251,146,60,0.1)",
  green:    "#26C16E",
  greenD:   "rgba(38,193,110,0.1)",
  red:      "#F87171",
  redD:     "rgba(248,113,113,0.1)",
  text:     "#EEF4FF",
  sub:      "#8BA8C8",
  muted:    "#3D5470",
  syne:     "'Rajdhani',sans-serif",
  inst:     "'Exo 2',sans-serif",
};

/* ── MOCK DATA ─────────────────────────────────────────────── */
const EXPERTS = [
  { id:1, name:"Ahsan Malik", title:"NLP & LLM Engineer", city:"Lahore", rating:4.9, jobs:23, badge:"Top Rated", skills:["LLM Fine-tuning","RAG","Urdu NLP","Python"], rate:"$45/hr", avatar:"AM", color:"#26C16E", verified:true, bio:"7 years building NLP systems for Pakistani banks and telecom. Specialist in Urdu language models.", availability:"Available now" },
  { id:2, name:"Zara Sheikh", title:"Computer Vision Expert", city:"Karachi", rating:4.8, jobs:18, badge:"Rising Star", skills:["YOLO","Defect Detection","OpenCV","PyTorch"], rate:"$40/hr", avatar:"ZS", color:"#388BF8", verified:true, bio:"Ex-NESCOM engineer. Built QC vision systems for 3 textile mills in Faisalabad.", availability:"Available in 1 week" },
  { id:3, name:"Bilal Chaudhry", title:"MLOps & Data Engineer", city:"Islamabad", rating:4.9, jobs:31, badge:"Expert", skills:["MLflow","AWS","FastAPI","Spark"], rate:"$55/hr", avatar:"BC", color:"#A78BFA", verified:true, bio:"Specialises in production ML pipelines. Has deployed 12+ models to Pakistani enterprise.", availability:"Available now" },
  { id:4, name:"Sana Iqbal", title:"AI Strategy Consultant", city:"Lahore", rating:5.0, jobs:14, badge:"Top Rated", skills:["AI Roadmaps","Change Mgmt","ROI Analysis","Agile"], rate:"$60/hr", avatar:"SI", color:"#4ADE80", verified:true, bio:"Former McKinsey analyst turned AI consultant. Helped 20+ Pakistani companies build AI strategies.", availability:"Available now" },
  { id:5, name:"Usman Raza", title:"Predictive Analytics Engineer", city:"Faisalabad", rating:4.7, jobs:9, badge:"Rising Star", skills:["Forecasting","Scikit-learn","Tableau","SQL"], rate:"$35/hr", avatar:"UR", color:"#FB923C", verified:false, bio:"Specialises in demand forecasting for retail and FMCG. Built systems for 4 major distributors.", availability:"Available in 2 weeks" },
  { id:6, name:"Fatima Baig", title:"Chatbot & Conversational AI", city:"Karachi", rating:4.8, jobs:21, badge:"Expert", skills:["Rasa","LangChain","WhatsApp API","Urdu"], rate:"$42/hr", avatar:"FB", color:"#F87171", verified:true, bio:"Built Urdu-language chatbots for SadaPay, Bykea, and 3 telecom companies.", availability:"Available now" },
];

const CLIENT_PROJECTS = [
  { id:1, title:"Demand Forecasting System", status:"In Progress", expert:"Bilal Chaudhry", progress:65, budget:"$8,500", due:"May 15", phase:"Development", messages:3 },
  { id:2, title:"Urdu Customer Support Bot", status:"Review", expert:"Fatima Baig", progress:90, budget:"$4,200", due:"Apr 30", phase:"Testing", messages:7 },
  { id:3, title:"AI Readiness Training", status:"Completed", expert:"Sana Iqbal", progress:100, budget:"$3,000", due:"Apr 10", phase:"Done", messages:0 },
];

const ADMIN_STATS = [
  { label:"Registered Businesses", value:"347", change:"+28 this month", color:C.accent, icon:"🏢" },
  { label:"Certified Experts", value:"89", change:"+12 this month", color:C.blue, icon:"👨‍💻" },
  { label:"Active Projects", value:"134", change:"+19 this month", color:C.purple, icon:"⚡" },
  { label:"Platform Revenue", value:"PKR 4.2M", change:"+31% MoM", color:C.green, icon:"💰" },
];

const PENDING_EXPERTS = [
  { name:"Hassan Ali", title:"ML Engineer", city:"Karachi", applied:"2 days ago", score:88 },
  { name:"Mariam Khan", title:"Data Scientist", city:"Lahore", applied:"3 days ago", score:91 },
  { name:"Tariq Mahmood", title:"AI Consultant", city:"Islamabad", applied:"5 days ago", score:76 },
];

/* ── REUSABLE UI ───────────────────────────────────────────── */
const Badge = ({label, color=C.accent}) => (
  <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:4,letterSpacing:"0.06em",
    background:`${color}18`,color,border:`1px solid ${color}30`,fontFamily:C.inst}}>
    {label}
  </span>
);

const Stat = ({label,value,change,color=C.accent,icon}) => (
  <div style={{padding:"18px 20px",borderRadius:12,background:C.surface,border:`1px solid ${C.border}`,flex:1,minWidth:140}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
      <span style={{fontSize:20}}>{icon}</span>
      <span style={{fontSize:11,color:C.green,fontFamily:C.inst,background:C.greenD,padding:"2px 7px",borderRadius:4}}>{change}</span>
    </div>
    <div style={{fontSize:26,fontWeight:800,color,fontFamily:C.syne,marginBottom:2}}>{value}</div>
    <div style={{fontSize:11,color:C.muted,fontFamily:C.inst,textTransform:"uppercase",letterSpacing:"0.07em"}}>{label}</div>
  </div>
);

const Avatar = ({initials,color="#26C16E",size=36}) => (
  <div style={{width:size,height:size,borderRadius:"50%",background:`${color}20`,
    border:`1.5px solid ${color}40`,display:"flex",alignItems:"center",justifyContent:"center",
    fontSize:size*0.32,fontWeight:700,color,fontFamily:C.syne,flexShrink:0}}>
    {initials}
  </div>
);

const ProgressBar = ({pct,color=C.accent}) => (
  <div style={{height:4,borderRadius:2,background:"rgba(255,255,255,0.07)",overflow:"hidden"}}>
    <div style={{height:"100%",width:`${pct}%`,borderRadius:2,background:color,transition:"width 1s ease"}}/>
  </div>
);

const Tag = ({label}) => (
  <span style={{fontSize:11,padding:"3px 9px",borderRadius:4,background:"rgba(255,255,255,0.04)",
    border:`1px solid ${C.border}`,color:C.sub,fontFamily:C.inst}}>{label}</span>
);

const StatusDot = ({status}) => {
  const map = {
    "In Progress":{c:C.blue,label:"In Progress"},
    "Review":{c:C.orange,label:"In Review"},
    "Completed":{c:C.green,label:"Completed"},
    "Available now":{c:C.green,label:"Available now"},
    "Available in 1 week":{c:C.accent,label:"Avail. 1 wk"},
    "Available in 2 weeks":{c:C.orange,label:"Avail. 2 wks"},
  };
  const s = map[status]||{c:C.muted,label:status};
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:11,color:s.c,fontFamily:C.inst}}>
      <span style={{width:6,height:6,borderRadius:"50%",background:s.c,display:"inline-block",
        boxShadow:status==="Available now"?`0 0 6px ${s.c}`:"none"}}
        className={status==="Available now"?"pulse":""}/>{s.label}
    </span>
  );
};

const SectionTitle = ({children,sub}) => (
  <div style={{marginBottom:20}}>
    <div style={{fontSize:11,fontWeight:700,letterSpacing:"0.09em",textTransform:"uppercase",
      color:C.muted,fontFamily:C.syne,marginBottom:4}}>{children}</div>
    {sub&&<div style={{fontSize:13,color:C.sub,fontFamily:C.inst}}>{sub}</div>}
  </div>
);

const Btn = ({children,onClick,variant="primary",small,disabled,style={}}) => {
  const base = {
    padding:small?"7px 14px":"10px 20px",borderRadius:8,border:"none",
    cursor:disabled?"not-allowed":"pointer",
    fontSize:small?12:13,fontWeight:600,fontFamily:C.inst,
    transition:"all 0.15s",letterSpacing:"0.01em",...style
  };
  const v = {
    primary:{background:disabled?"rgba(255,255,255,0.06)":C.grad,color:disabled?C.muted:"#fff",border:"none"},
    ghost:{background:"transparent",border:`1px solid ${C.border}`,color:C.sub},
    blue:{background:C.blueD,color:C.blue,border:`1px solid rgba(56,139,248,0.25)`},
    danger:{background:C.redD,color:C.red,border:`1px solid rgba(248,113,113,0.2)`},
    green:{background:C.greenD,color:C.green,border:`1px solid rgba(38,193,110,0.25)`},
  };
  return <button onClick={!disabled?onClick:undefined} style={{...base,...(v[variant]||v.primary)}}>{children}</button>;
};

/* ── SIDEBAR ───────────────────────────────────────────────── */
const NAV_ITEMS = [
  {id:"overview", icon:"⬡", label:"Platform"},
  {id:"audit",    icon:"🔍", label:"AI Audit"},
  {id:"client",   icon:"🏢", label:"Client View"},
  {id:"expert",   icon:"👨‍💻", label:"Expert View"},
  {id:"admin",    icon:"⚙️", label:"Admin Panel"},
];

const Sidebar = ({active,setActive}) => (
  <div style={{width:220,background:C.bg2,borderRight:`1px solid ${C.border}`,
    display:"flex",flexDirection:"column",height:"100vh",position:"sticky",top:0,flexShrink:0}}>
    <div style={{padding:"20px 18px 16px",borderBottom:`1px solid ${C.border}`}}>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <div style={{width:34,height:34,borderRadius:9,background:"rgba(38,193,110,0.12)",
          border:"1px solid rgba(38,193,110,0.3)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:17}}>
          🇵🇰
        </div>
        <div>
          <div style={{fontSize:14,fontWeight:700,fontFamily:C.syne,letterSpacing:"0.04em"}}>
            <span style={{background:C.grad,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>AI</span>
            <span style={{color:C.text}}>PARTNERS</span>
            <span style={{background:C.grad,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>.PK</span>
          </div>
          <div style={{fontSize:9,color:C.muted,fontFamily:C.inst,letterSpacing:"0.08em",textTransform:"uppercase",marginTop:1}}>
            AI Marketplace
          </div>
        </div>
      </div>
    </div>
    <nav style={{flex:1,padding:"14px 12px",display:"flex",flexDirection:"column",gap:3}}>
      {NAV_ITEMS.map(n=>{
        const on = active===n.id;
        return (
          <button key={n.id} onClick={()=>setActive(n.id)} style={{
            display:"flex",alignItems:"center",gap:10,padding:"9px 10px",borderRadius:8,
            border:"none",cursor:"pointer",textAlign:"left",width:"100%",transition:"all 0.15s",
            background:on?"rgba(38,193,110,0.08)":"transparent",
          }}>
            <span style={{fontSize:15,width:20,textAlign:"center"}}>{n.icon}</span>
            <span style={{fontSize:13,fontWeight:on?600:400,color:on?C.accent:C.sub,fontFamily:C.inst}}>{n.label}</span>
            {on&&<div style={{marginLeft:"auto",width:3,height:16,borderRadius:2,background:C.accent}}/>}
          </button>
        );
      })}
    </nav>
    <div style={{padding:"14px 16px",borderTop:`1px solid ${C.border}`}}>
      <div style={{fontSize:10,color:C.muted,fontFamily:C.inst,textAlign:"center",lineHeight:1.6}}>
        <span style={{background:C.grad,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",fontWeight:700}}>AIpartners.pk</span><br/>
        Pakistan's First AI Marketplace
      </div>
    </div>
  </div>
);

/* ── PLATFORM OVERVIEW ─────────────────────────────────────── */
const PlatformOverview = ({setActive}) => (
  <div style={{padding:"32px 36px",maxWidth:960,margin:"0 auto"}}>
    <div className="fu" style={{marginBottom:36}}>
      <Badge label="PLATFORM ARCHITECTURE" color={C.accent}/>
      <h1 style={{fontSize:34,fontWeight:800,color:C.text,fontFamily:C.syne,marginTop:12,letterSpacing:"0.01em",lineHeight:1.15}}>
        Pakistan's First<br/>
        <span style={{background:C.grad,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>
          AI Talent Marketplace
        </span>
      </h1>
      <p style={{fontSize:15,color:C.sub,marginTop:12,fontFamily:C.inst,maxWidth:560,lineHeight:1.7}}>
        Businesses get a free AI audit, then get matched with certified AI experts who build their solutions. You earn a platform fee on every project.
      </p>
    </div>
    <div style={{marginBottom:36,padding:"28px",borderRadius:16,background:C.surface,border:`1px solid ${C.border}`}}>
      <SectionTitle>How the Platform Works</SectionTitle>
      <div style={{display:"flex",alignItems:"center",gap:0,overflowX:"auto",paddingBottom:4}}>
        {[
          {step:"1",icon:"📋",title:"AI Audit",desc:"Business completes free audit. AI scores 6 dimensions of readiness.",color:C.accent},
          {step:"2",icon:"🤖",title:"AI Matching",desc:"Claude analyses audit + recommends 3 best-fit certified experts.",color:C.blue},
          {step:"3",icon:"🤝",title:"Hire Expert",desc:"Client reviews profiles, confirms match, signs scoped contract.",color:C.purple},
          {step:"4",icon:"⚙️",title:"Build & Deploy",desc:"Expert builds the AI solution. You supervise quality & delivery.",color:C.orange},
          {step:"5",icon:"💰",title:"Platform Fee",desc:"You collect 15–20% of every contract. Expert gets rest.",color:C.green},
        ].map((s,i,arr)=>(
          <div key={i} style={{display:"flex",alignItems:"center",flex:1,minWidth:140}}>
            <div style={{flex:1,padding:"16px 14px",borderRadius:10,background:`${s.color}08`,border:`1px solid ${s.color}25`,textAlign:"center"}}>
              <div style={{fontSize:24,marginBottom:8}}>{s.icon}</div>
              <div style={{fontSize:11,fontWeight:700,color:s.color,fontFamily:C.syne,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:6}}>{s.step}. {s.title}</div>
              <div style={{fontSize:11,color:C.sub,fontFamily:C.inst,lineHeight:1.5}}>{s.desc}</div>
            </div>
            {i<arr.length-1&&<div style={{fontSize:18,color:C.muted,padding:"0 6px",flexShrink:0}}>›</div>}
          </div>
        ))}
      </div>
    </div>
    <SectionTitle sub="Click any portal to explore that dashboard">Three Portals, One Ecosystem</SectionTitle>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14,marginBottom:32}}>
      {[
        {id:"client",icon:"🏢",title:"Client Portal",color:C.accent,points:["Complete AI audit","View AI-matched experts","Track project progress","Chat with expert","Review & approve deliverables"],who:"For: Businesses & Industry Owners"},
        {id:"expert",icon:"👨‍💻",title:"Expert Marketplace",color:C.blue,points:["Create certified profile","Get AI-matched to projects","Submit proposals","Deliver milestones","Build Pakistan reputation"],who:"For: AI Engineers & Consultants"},
        {id:"admin",icon:"⚙️",title:"Admin Panel",color:C.purple,points:["Certify & vet experts","Monitor all projects","Handle disputes","See platform revenue","Manage audit quality"],who:"For: Pak AI Partners Team"},
      ].map(p=>(
        <button key={p.id} onClick={()=>setActive(p.id)} style={{
          padding:"22px 20px",borderRadius:14,background:C.surface,border:`1px solid ${C.border}`,
          textAlign:"left",cursor:"pointer",transition:"all 0.2s",display:"block",width:"100%"}}
          onMouseEnter={e=>{e.currentTarget.style.border=`1px solid ${p.color}40`;e.currentTarget.style.background=`${p.color}06`}}
          onMouseLeave={e=>{e.currentTarget.style.border=`1px solid ${C.border}`;e.currentTarget.style.background=C.surface}}>
          <div style={{fontSize:28,marginBottom:12}}>{p.icon}</div>
          <div style={{fontSize:15,fontWeight:700,color:p.color,fontFamily:C.syne,marginBottom:4}}>{p.title}</div>
          <div style={{fontSize:10,color:C.muted,fontFamily:C.inst,marginBottom:14,letterSpacing:"0.04em"}}>{p.who}</div>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            {p.points.map((pt,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:7}}>
                <span style={{color:p.color,fontSize:11}}>›</span>
                <span style={{fontSize:12,color:C.sub,fontFamily:C.inst}}>{pt}</span>
              </div>
            ))}
          </div>
          <div style={{marginTop:16,fontSize:12,color:p.color,fontFamily:C.inst,fontWeight:600}}>Explore dashboard →</div>
        </button>
      ))}
    </div>
    <div style={{marginBottom:20,padding:"18px 20px",borderRadius:12,background:C.surface,border:`1px solid ${C.border}`,textAlign:"center"}}>
      <div style={{fontSize:14,fontWeight:700,color:C.accent,fontFamily:C.syne,marginBottom:8}}>
        🔍 Try the Free AI Audit — Takes 2 Minutes
      </div>
      <div style={{fontSize:13,color:C.sub,fontFamily:C.inst,marginBottom:14}}>
        Get your business's AI readiness score across 6 dimensions with a full report.
      </div>
      <Btn onClick={()=>setActive("audit")}>Start Free AI Audit →</Btn>
    </div>
    <div style={{padding:"22px 24px",borderRadius:14,background:C.surface,border:`1px solid ${C.border}`}}>
      <SectionTitle>Revenue Model for AIpartners.pk</SectionTitle>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
        {[
          {label:"Platform Commission",value:"15–20%",sub:"of every project contract",color:C.accent},
          {label:"Expert Certification",value:"PKR 25K",sub:"one-time vetting fee per expert",color:C.blue},
          {label:"Enterprise Subscription",value:"$299/mo",sub:"priority matching + dedicated PM",color:C.purple},
          {label:"Audit Pro Reports",value:"PKR 15K",sub:"detailed PDF report + consultation",color:C.green},
        ].map((r,i)=>(
          <div key={i} style={{padding:"14px 16px",borderRadius:10,background:`${r.color}08`,border:`1px solid ${r.color}20`}}>
            <div style={{fontSize:18,fontWeight:800,color:r.color,fontFamily:C.syne,marginBottom:4}}>{r.value}</div>
            <div style={{fontSize:11,fontWeight:700,color:C.text,fontFamily:C.inst,marginBottom:3}}>{r.label}</div>
            <div style={{fontSize:11,color:C.muted,fontFamily:C.inst}}>{r.sub}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ── LEAD CAPTURE COMPONENT ────────────────────────────────── */
// Uses Web3Forms (free, no backend needed) — sign up at web3forms.com
// Replace YOUR_ACCESS_KEY below with your key from web3forms.com
const WEB3FORMS_KEY = "YOUR_ACCESS_KEY_HERE";

const LeadCapture = ({form, result, onReset}) => {
  const [leadState, setLeadState] = useState("cta"); // cta | form | sending | success | error
  const [email, setEmail]   = useState("");
  const [phone, setPhone]   = useState("");
  const [name,  setName]    = useState("");
  const [sendErr, setSendErr] = useState("");

  const scoreColor = result
    ? result.overallScore>=75 ? C.green
      : result.overallScore>=50 ? C.accent
      : result.overallScore>=30 ? C.orange : C.red
    : C.accent;

  const submitLead = async () => {
    if(!email || !name) return;
    setLeadState("sending");
    setSendErr("");

    // Build a readable summary to send to your inbox
    const auditSummary = `
AI AUDIT LEAD — AIpartners.pk
================================
Company:  ${form.companyName}
Industry: ${form.industry}
City:     ${form.city}, Pakistan
Size:     ${form.companySize}
Revenue:  ${form.revenue}
Budget:   ${form.budget}
Urgency:  ${form.urgency}

AI READINESS SCORE: ${result?.overallScore}/100 (${result?.readinessLabel})

CONTACT DETAILS:
Name:  ${name}
Email: ${email}
Phone: ${phone || "Not provided"}

TOP QUICK WIN:
${result?.quickWins?.[0]?.title || "See full report"}
ROI: ${result?.quickWins?.[0]?.roiEstimate || ""}

CHALLENGES:
${form.challenges.join(", ")}

GOALS:
${form.goals.join(", ")}

Submitted: ${new Date().toLocaleString("en-PK", {timeZone:"Asia/Karachi"})} PKT
================================
    `.trim();

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {"Content-Type":"application/json"},
        body: JSON.stringify({
          access_key:   WEB3FORMS_KEY,
          subject:      `🚀 New AI Audit Lead — ${form.companyName} (Score: ${result?.overallScore}/100)`,
          from_name:    "AIpartners.pk Audit System",
          email:        email,
          name:         name,
          phone:        phone || "Not provided",
          company:      form.companyName,
          industry:     form.industry,
          city:         form.city,
          score:        `${result?.overallScore}/100 — ${result?.readinessLabel}`,
          message:      auditSummary,
          botcheck:     "",
        })
      });
      const data = await res.json();
      if(data.success) {
        setLeadState("success");
      } else {
        setSendErr("Submission failed. Please try again or email us directly at hello@aipartners.pk");
        setLeadState("error");
      }
    } catch(e) {
      setSendErr("Network error. Please email us at hello@aipartners.pk");
      setLeadState("error");
    }
  };

  // ── CTA STATE — shown first after report loads
  if(leadState === "cta") return (
    <div style={{borderRadius:14,border:`1px solid ${C.accentB}`,
      background:"linear-gradient(135deg,rgba(38,193,110,0.07),rgba(56,139,248,0.07))",
      padding:"28px",marginTop:4}}>

      {/* Score summary row */}
      <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:20,
        padding:"14px 18px",borderRadius:10,background:"rgba(0,0,0,0.2)"}}>
        <div style={{textAlign:"center",flexShrink:0}}>
          <div style={{fontSize:36,fontWeight:800,color:scoreColor,fontFamily:C.syne,lineHeight:1}}>{result?.overallScore}</div>
          <div style={{fontSize:9,color:C.muted,fontFamily:C.inst,textTransform:"uppercase",letterSpacing:"0.07em"}}>AI Score</div>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:14,fontWeight:700,color:C.text,fontFamily:C.syne,marginBottom:4}}>
            {form.companyName} — Audit Complete
          </div>
          <div style={{fontSize:12,color:C.sub,fontFamily:C.inst,lineHeight:1.5}}>
            Your report is ready. Enter your details below to receive a copy and get contacted by our team within 24 hours.
          </div>
        </div>
      </div>

      {/* What you get */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:20}}>
        {[
          {icon:"📄", text:"Full PDF audit report emailed to you"},
          {icon:"📞", text:"Free 30-min consultation call"},
          {icon:"🗺️", text:"Custom 18-month AI roadmap"},
          {icon:"💰", text:"ROI projection for your business"},
        ].map((item,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",
            borderRadius:8,background:"rgba(255,255,255,0.03)",border:`1px solid ${C.border}`}}>
            <span style={{fontSize:16,flexShrink:0}}>{item.icon}</span>
            <span style={{fontSize:12,color:C.sub,fontFamily:C.inst}}>{item.text}</span>
          </div>
        ))}
      </div>

      <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
        <Btn onClick={()=>setLeadState("form")} style={{minWidth:200}}>
          📩 Get My Free Report →
        </Btn>
        <Btn variant="ghost" onClick={onReset}>Run Another Audit</Btn>
      </div>
      <div style={{textAlign:"center",marginTop:12,fontSize:11,color:C.muted,fontFamily:C.inst}}>
        🔒 Your data is confidential · No spam · Response within 24 hours
      </div>
    </div>
  );

  // ── FORM STATE — collect name, email, phone
  if(leadState === "form") return (
    <div style={{borderRadius:14,border:`1px solid ${C.accentB}`,
      background:"linear-gradient(135deg,rgba(38,193,110,0.07),rgba(56,139,248,0.07))",padding:"28px"}} className="fi">

      <div style={{fontSize:18,fontWeight:800,color:C.text,fontFamily:C.syne,marginBottom:4}}>
        Get Your Full Report
      </div>
      <div style={{fontSize:13,color:C.sub,fontFamily:C.inst,marginBottom:20}}>
        We'll email your complete AI Opportunities Report and reach out within 24 hours.
      </div>

      {/* Pre-filled company info — shows user what we already have */}
      <div style={{padding:"10px 14px",borderRadius:8,background:"rgba(38,193,110,0.06)",
        border:`1px solid ${C.accentB}`,marginBottom:18,display:"flex",gap:16,flexWrap:"wrap"}}>
        <span style={{fontSize:11,color:C.accent,fontFamily:C.inst}}>✓ {form.companyName}</span>
        <span style={{fontSize:11,color:C.accent,fontFamily:C.inst}}>✓ {form.industry}</span>
        <span style={{fontSize:11,color:C.accent,fontFamily:C.inst}}>✓ {form.city}</span>
        <span style={{fontSize:11,color:C.accent,fontFamily:C.inst}}>✓ Score: {result?.overallScore}/100</span>
      </div>

      {/* Name */}
      <div style={{marginBottom:14}}>
        <div style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:"0.08em",textTransform:"uppercase",fontFamily:C.syne,marginBottom:6}}>
          Your Full Name *
        </div>
        <input value={name} onChange={e=>setName(e.target.value)}
          placeholder="e.g. Ahmed Khan"
          style={{width:"100%",padding:"11px 14px",borderRadius:8,border:`1px solid ${name?C.accentB:C.border}`,
            background:"rgba(255,255,255,0.04)",color:C.text,fontSize:14,fontFamily:C.inst,outline:"none",boxSizing:"border-box"}}/>
      </div>

      {/* Email */}
      <div style={{marginBottom:14}}>
        <div style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:"0.08em",textTransform:"uppercase",fontFamily:C.syne,marginBottom:6}}>
          Work Email Address *
        </div>
        <input value={email} onChange={e=>setEmail(e.target.value)}
          placeholder="e.g. ahmed@company.com"
          type="email"
          style={{width:"100%",padding:"11px 14px",borderRadius:8,border:`1px solid ${email?C.accentB:C.border}`,
            background:"rgba(255,255,255,0.04)",color:C.text,fontSize:14,fontFamily:C.inst,outline:"none",boxSizing:"border-box"}}/>
      </div>

      {/* Phone */}
      <div style={{marginBottom:22}}>
        <div style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:"0.08em",textTransform:"uppercase",fontFamily:C.syne,marginBottom:6}}>
          Phone Number <span style={{color:C.muted,fontWeight:400,textTransform:"none"}}>(optional — for WhatsApp)</span>
        </div>
        <input value={phone} onChange={e=>setPhone(e.target.value)}
          placeholder="e.g. +92 300 1234567"
          type="tel"
          style={{width:"100%",padding:"11px 14px",borderRadius:8,border:`1px solid ${phone?C.accentB:C.border}`,
            background:"rgba(255,255,255,0.04)",color:C.text,fontSize:14,fontFamily:C.inst,outline:"none",boxSizing:"border-box"}}/>
      </div>

      <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
        <Btn variant="ghost" onClick={()=>setLeadState("cta")}>← Back</Btn>
        <Btn onClick={submitLead} disabled={!email||!name} style={{flex:1,textAlign:"center"}}>
          Send Me My Report →
        </Btn>
      </div>
      <div style={{textAlign:"center",marginTop:12,fontSize:11,color:C.muted,fontFamily:C.inst}}>
        🔒 We never share your data · No spam ever · Unsubscribe anytime
      </div>
    </div>
  );

  // ── SENDING STATE
  if(leadState === "sending") return (
    <div style={{borderRadius:14,border:`1px solid ${C.accentB}`,
      background:"linear-gradient(135deg,rgba(38,193,110,0.07),rgba(56,139,248,0.07))",
      padding:"40px 28px",textAlign:"center"}} className="fi">
      <div style={{fontSize:32,marginBottom:16}}>📨</div>
      <div style={{fontSize:16,fontWeight:700,color:C.text,fontFamily:C.syne,marginBottom:8}}>Sending your report...</div>
      <div style={{display:"flex",gap:4,justifyContent:"center"}}>
        {[0,1,2,3,4].map(i=>(
          <div key={i} style={{width:6,height:6,borderRadius:"50%",background:C.accent,
            animation:`pulse 1.2s ease ${i*0.15}s infinite`}}/>
        ))}
      </div>
    </div>
  );

  // ── SUCCESS STATE
  if(leadState === "success") return (
    <div style={{borderRadius:14,border:`1px solid rgba(38,193,110,0.4)`,
      background:"linear-gradient(135deg,rgba(38,193,110,0.08),rgba(56,139,248,0.05))",
      padding:"32px 28px",textAlign:"center"}} className="fi">
      <div style={{fontSize:40,marginBottom:12}}>🎉</div>
      <div style={{fontSize:20,fontWeight:800,color:C.green,fontFamily:C.syne,marginBottom:8}}>
        You're All Set!
      </div>
      <div style={{fontSize:14,color:C.text,fontFamily:C.inst,marginBottom:6}}>
        Check your inbox — your AI report is on its way to <strong style={{color:C.accent}}>{email}</strong>
      </div>
      <div style={{fontSize:13,color:C.sub,fontFamily:C.inst,marginBottom:24,lineHeight:1.6}}>
        Our team will review your audit results and contact you within 24 hours to discuss your AI transformation roadmap.
      </div>

      {/* What happens next */}
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:24,textAlign:"left"}}>
        {[
          {step:"1", text:"Check your email for the full PDF report", time:"In minutes"},
          {step:"2", text:"Our team reviews your audit score & findings", time:"Within 2 hours"},
          {step:"3", text:"You receive a personalised proposal & consultation invite", time:"Within 24 hours"},
        ].map((s,i)=>(
          <div key={i} style={{display:"flex",gap:12,alignItems:"center",padding:"10px 14px",
            borderRadius:8,background:"rgba(255,255,255,0.03)",border:`1px solid ${C.border}`}}>
            <div style={{width:24,height:24,borderRadius:"50%",background:C.accentD,
              border:`1px solid ${C.accentB}`,display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:11,fontWeight:800,color:C.accent,fontFamily:C.syne,flexShrink:0}}>{s.step}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:13,color:C.text,fontFamily:C.inst}}>{s.text}</div>
            </div>
            <div style={{fontSize:11,color:C.muted,fontFamily:C.inst,flexShrink:0}}>{s.time}</div>
          </div>
        ))}
      </div>

      {/* WhatsApp CTA if phone provided */}
      {phone&&(
        <div style={{padding:"12px 16px",borderRadius:8,background:"rgba(37,211,102,0.08)",
          border:"1px solid rgba(37,211,102,0.2)",marginBottom:16}}>
          <span style={{fontSize:12,color:"#25D366",fontFamily:C.inst}}>
            📱 We'll also reach out on WhatsApp at {phone} for a quick chat.
          </span>
        </div>
      )}

      <Btn variant="ghost" onClick={onReset} style={{width:"100%",textAlign:"center"}}>
        Run Another Audit →
      </Btn>
    </div>
  );

  // ── ERROR STATE
  if(leadState === "error") return (
    <div style={{borderRadius:14,border:`1px solid rgba(248,113,113,0.3)`,
      background:"rgba(248,113,113,0.05)",padding:"28px",textAlign:"center"}} className="fi">
      <div style={{fontSize:32,marginBottom:12}}>⚠️</div>
      <div style={{fontSize:15,fontWeight:700,color:C.red,fontFamily:C.syne,marginBottom:8}}>Submission Error</div>
      <div style={{fontSize:13,color:C.sub,fontFamily:C.inst,marginBottom:20}}>{sendErr}</div>
      <div style={{display:"flex",gap:10,justifyContent:"center"}}>
        <Btn onClick={()=>{setLeadState("form");setSendErr("");}}>Try Again</Btn>
        <Btn variant="ghost" onClick={()=>window.open("mailto:hello@aipartners.pk?subject=AI Audit Lead — "+form.companyName,"_blank")}>
          Email Us Directly
        </Btn>
      </div>
    </div>
  );

  return null;
};

/* ── AI AUDIT PAGE ─────────────────────────────────────────── */
const INDUSTRIES_LIST = ["Manufacturing & Textile","Agriculture & Food","Healthcare & Pharma","Retail & E-commerce","Finance & Banking","Logistics & Supply Chain","Education","Real Estate","Energy & Utilities","Construction","Other"];
const SIZES_LIST = ["1–10 employees","11–50 employees","51–200 employees","201–1000 employees","1000+ employees"];
const CITIES_LIST = ["Karachi","Lahore","Islamabad / Rawalpindi","Faisalabad","Multan","Peshawar","Quetta","Sialkot","Gujranwala","Other"];
const REVENUES_LIST = ["Under PKR 10M","PKR 10M–50M","PKR 50M–200M","PKR 200M–1B","PKR 1B+","Prefer not to say"];
const BUDGETS_LIST = ["Under $3,000","$3,000–$10,000","$10,000–$50,000","$50,000–$200,000","$200,000+","Depends on ROI proof"];
const CHALLENGES_LIST = [
  {id:"c1",label:"Manual repetitive tasks",icon:"🔁"},{id:"c2",label:"High error / defect rates",icon:"⚠️"},
  {id:"c3",label:"Slow customer response",icon:"💬"},{id:"c4",label:"Demand forecasting failures",icon:"📉"},
  {id:"c5",label:"Fraud & financial risk",icon:"🔐"},{id:"c6",label:"Supply chain inefficiencies",icon:"🚚"},
  {id:"c7",label:"No data-driven decisions",icon:"📊"},{id:"c8",label:"High HR / staffing costs",icon:"👥"},
  {id:"c9",label:"Quality control issues",icon:"🔬"},{id:"c10",label:"Language barriers (Urdu/English)",icon:"🗣️"},
  {id:"c11",label:"Inventory mismanagement",icon:"📦"},{id:"c12",label:"Slow financial reporting",icon:"📑"},
];
const GOALS_LIST = [
  {id:"g1",label:"Cut operational costs",icon:"✂️"},{id:"g2",label:"Increase revenue",icon:"📈"},
  {id:"g3",label:"Automate repetitive work",icon:"🤖"},{id:"g4",label:"Better customer experience",icon:"⭐"},
  {id:"g5",label:"Scale without hiring",icon:"🚀"},{id:"g6",label:"Data-driven decisions",icon:"🧠"},
  {id:"g7",label:"Compete with larger firms",icon:"🏆"},{id:"g8",label:"Regulatory compliance",icon:"📋"},
];
const TECH_OPTIONS = [
  {v:"0",label:"Paper-based / no digital systems"},{v:"2",label:"Basic — Excel, email, WhatsApp"},
  {v:"4",label:"Moderate — some ERP/software"},{v:"7",label:"Advanced — cloud systems, APIs"},
  {v:"9",label:"Cutting-edge — data pipelines & analytics"},
];
const DATA_OPTIONS = [
  {v:"0",label:"No data collected"},{v:"2",label:"Some records in Excel/paper"},
  {v:"5",label:"Structured data in a system"},{v:"7",label:"Multi-source data, partially integrated"},
  {v:"9",label:"Clean, rich, integrated data warehouse"},
];
const URGENCY_LIST = [
  {v:"explore",label:"Just exploring — no timeline"},{v:"6mo",label:"Want to start within 6 months"},
  {v:"3mo",label:"Ready to move in 1–3 months"},{v:"now",label:"Urgent — need solutions now"},
];
const SCAN_STEPS = [
  "Parsing company profile & industry context",
  "Calculating 6-dimension AI readiness score",
  "Matching Pakistan industry benchmarks",
  "Identifying highest-ROI AI use cases",
  "Estimating implementation effort & cost",
  "Generating prioritised roadmap",
  "Compiling your custom audit report",
];

const AuditPage = ({setActive}) => {
  const [step,setStep] = useState(0);
  const [form,setForm] = useState({
    companyName:"",industry:"",companySize:"",city:"",revenue:"",
    techLevel:"",dataMaturity:"",budget:"",urgency:"",
    challenges:[],goals:[],additionalInfo:""
  });
  const [scanIdx,setScanIdx] = useState(0);
  const [result,setResult] = useState(null);
  const [error,setError] = useState(null);
  const [running,setRunning] = useState(false);

  const upd = (k,v) => setForm(f=>({...f,[k]:v}));
  const tog = (k,v) => setForm(f=>({...f,[k]:f[k].includes(v)?f[k].filter(x=>x!==v):[...f[k],v]}));

  const canNext = () => {
    if(step===0) return form.companyName&&form.industry&&form.companySize&&form.city&&form.revenue;
    if(step===1) return form.challenges.length>=2;
    if(step===2) return form.techLevel&&form.dataMaturity&&form.budget;
    if(step===3) return form.goals.length>=1&&form.urgency;
    return true;
  };

  const ASelect = ({options,value,onChange,placeholder}) => (
    <select value={value} onChange={e=>onChange(e.target.value)} style={{
      width:"100%",padding:"10px 14px",borderRadius:8,border:`1px solid ${C.border}`,
      background:"rgba(255,255,255,0.04)",color:value?C.text:C.muted,fontSize:14,
      fontFamily:C.inst,outline:"none",appearance:"none",cursor:"pointer"}}>
      <option value="" disabled>{placeholder}</option>
      {options.map(o=><option key={o} value={o} style={{background:"#0D1117"}}>{o}</option>)}
    </select>
  );

  const AInput = ({value,onChange,placeholder}) => (
    <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={{
      width:"100%",padding:"10px 14px",borderRadius:8,border:`1px solid ${C.border}`,
      background:"rgba(255,255,255,0.04)",color:C.text,fontSize:14,fontFamily:C.inst,outline:"none"}}/>
  );

  const Chip = ({label,icon,selected,onClick}) => (
    <button onClick={onClick} style={{
      display:"flex",alignItems:"center",gap:6,padding:"8px 12px",borderRadius:8,
      border:`1px solid ${selected?C.accentB:C.border}`,
      background:selected?C.accentD:"rgba(255,255,255,0.02)",
      color:selected?C.accent:C.sub,cursor:"pointer",fontSize:12,fontFamily:C.inst,transition:"all 0.15s"}}>
      {icon&&<span style={{fontSize:13}}>{icon}</span>}{label}
    </button>
  );

  const Radio = ({label,selected,onClick}) => (
    <button onClick={onClick} style={{
      width:"100%",padding:"10px 14px",borderRadius:8,textAlign:"left",
      border:`1px solid ${selected?C.accentB:C.border}`,
      background:selected?C.accentD:"rgba(255,255,255,0.02)",
      cursor:"pointer",transition:"all 0.15s",display:"flex",alignItems:"center",gap:10}}>
      <div style={{width:15,height:15,borderRadius:"50%",border:`2px solid ${selected?C.accent:C.muted}`,
        display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        {selected&&<div style={{width:6,height:6,borderRadius:"50%",background:C.accent}}/>}
      </div>
      <span style={{fontSize:13,color:selected?C.accent:C.text,fontFamily:C.inst}}>{label}</span>
    </button>
  );

  const runAudit = async () => {
    setRunning(true);
    setStep(4);
    setScanIdx(0);
    let i=0;
    const iv=setInterval(()=>{i++;setScanIdx(i);if(i>=SCAN_STEPS.length-1)clearInterval(iv);},600);

    const challengeLabels = form.challenges.map(id=>CHALLENGES_LIST.find(c=>c.id===id)?.label||id);
    const goalLabels = form.goals.map(id=>GOALS_LIST.find(g=>g.id===id)?.label||id);
    const techLabel = TECH_OPTIONS.find(o=>o.v===form.techLevel)?.label||form.techLevel;
    const dataLabel = DATA_OPTIONS.find(o=>o.v===form.dataMaturity)?.label||form.dataMaturity;
    const urgencyLabel = URGENCY_LIST.find(o=>o.v===form.urgency)?.label||form.urgency;

    const prompt = `You are a senior AI transformation consultant specialising in Pakistan's industry landscape. Conduct a deep AI readiness audit.

COMPANY PROFILE:
- Name: ${form.companyName}
- Industry: ${form.industry}
- Size: ${form.companySize}
- City: ${form.city}, Pakistan
- Annual Revenue: ${form.revenue}
- Tech Level: ${techLabel}
- Data Maturity: ${dataLabel}
- AI Budget: ${form.budget}
- Urgency: ${urgencyLabel}
- Challenges: ${challengeLabels.join(", ")}
- Goals: ${goalLabels.join(", ")}
- Context: ${form.additionalInfo||"None"}

Return ONLY valid JSON, no markdown:
{
  "overallScore": <0-100>,
  "readinessLabel": "<Beginner|Developing|Ready|Advanced>",
  "dimensions": [
    {"label":"Data Readiness","score":<0-100>,"insight":"<1 sentence>"},
    {"label":"Tech Infrastructure","score":<0-100>,"insight":"<1 sentence>"},
    {"label":"Process Maturity","score":<0-100>,"insight":"<1 sentence>"},
    {"label":"Leadership Buy-in","score":<0-100>,"insight":"<1 sentence>"},
    {"label":"Talent & Skills","score":<0-100>,"insight":"<1 sentence>"},
    {"label":"Financial Readiness","score":<0-100>,"insight":"<1 sentence>"}
  ],
  "executiveSummary": "<3-4 sentences specific to their industry and Pakistan context>",
  "competitiveAlert": "<1-2 sentences on what competitors in Pakistan are doing with AI>",
  "quickWins": [
    {"rank":1,"title":"<AI solution>","problem":"<pain point solved>","solution":"<what it does>","roiEstimate":"<specific PKR or %>","effort":"Low|Medium","timeline":"<weeks>","cost":"<USD range>","pakistanNote":"<local context>"},
    {"rank":2,"title":"<AI solution>","problem":"<pain point>","solution":"<what it does>","roiEstimate":"<PKR or %>","effort":"Low|Medium","timeline":"<weeks>","cost":"<USD>","pakistanNote":"<local context>"},
    {"rank":3,"title":"<AI solution>","problem":"<pain point>","solution":"<what it does>","roiEstimate":"<PKR or %>","effort":"Medium","timeline":"<weeks>","cost":"<USD>","pakistanNote":"<local context>"}
  ],
  "strategicRoadmap": [
    {"phase":"Phase 1 — Foundation (0-3 months)","objective":"<goal>","initiatives":["<item1>","<item2>"],"investment":"<USD>","milestone":"<outcome>"},
    {"phase":"Phase 2 — Acceleration (3-9 months)","objective":"<goal>","initiatives":["<item1>","<item2>","<item3>"],"investment":"<USD>","milestone":"<outcome>"},
    {"phase":"Phase 3 — Scale (9-18 months)","objective":"<goal>","initiatives":["<item1>","<item2>"],"investment":"<USD>","milestone":"<outcome>"}
  ],
  "topRisks": [
    {"risk":"<title>","description":"<Pakistan-specific detail>","mitigation":"<action>"},
    {"risk":"<title>","description":"<detail>","mitigation":"<action>"},
    {"risk":"<title>","description":"<detail>","mitigation":"<action>"}
  ],
  "estimatedROI": {"year1":"<efficiency gain>","year2":"<PKR saved>","paybackPeriod":"<months>"}
}`;

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          model:"claude-sonnet-4-20250514",
          max_tokens:1000,
          messages:[{role:"user",content:prompt}]
        })
      });
      const data = await res.json();
      const raw = data.content?.[0]?.text||"{}";
      const clean = raw.replace(/```json|```/g,"").trim();
      setResult(JSON.parse(clean));
    } catch(e) {
      setError("Audit generation encountered an error. Please try again.");
    }
    setRunning(false);
  };

  const scoreColor = result ? (result.overallScore>=75?"#4ADE80":result.overallScore>=50?C.accent:result.overallScore>=30?C.orange:C.red) : C.accent;

  const AuditLabel = ({children}) => (
    <div style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:"0.09em",textTransform:"uppercase",fontFamily:C.syne,marginBottom:8}}>{children}</div>
  );

  return (
    <div style={{minHeight:"100vh",background:C.bg,padding:32}}>
      <div style={{maxWidth:640,margin:"0 auto"}}>

        {/* Header */}
        <div style={{marginBottom:28,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{display:"inline-flex",alignItems:"center",gap:8,padding:"4px 12px",borderRadius:20,
              background:C.accentD,border:`1px solid ${C.accentB}`,marginBottom:10}}>
              <div style={{width:6,height:6,borderRadius:"50%",background:C.accent,boxShadow:`0 0 6px ${C.accent}`}} className="pulse"/>
              <span style={{fontSize:11,color:C.accent,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",fontFamily:C.syne}}>Free AI Readiness Audit</span>
            </div>
            <h1 style={{fontSize:26,fontWeight:800,color:C.text,fontFamily:C.syne,letterSpacing:"0.01em"}}>
              {step<4 ? "Is Your Business Ready for AI?" : step===4&&result ? `${form.companyName} — Audit Report` : "Running Your Audit..."}
            </h1>
          </div>
          <button onClick={()=>setActive("overview")} style={{background:"transparent",border:`1px solid ${C.border}`,color:C.sub,borderRadius:8,padding:"7px 14px",cursor:"pointer",fontSize:12,fontFamily:C.inst}}>← Back</button>
        </div>

        {/* Progress Bar */}
        {step<4&&(
          <div style={{display:"flex",gap:5,marginBottom:28}}>
            {[0,1,2,3].map(i=>(
              <div key={i} style={{flex:1,height:2,borderRadius:2,transition:"background 0.4s",
                background:i<step?C.accent:i===step?"rgba(38,193,110,0.4)":"rgba(255,255,255,0.07)"}}/>
            ))}
          </div>
        )}

        {/* STEP 0 — Company Info */}
        {step===0&&(
          <div style={{background:"rgba(255,255,255,0.025)",border:`1px solid ${C.border}`,borderRadius:16,padding:"28px"}} className="fi">
            <h2 style={{fontSize:20,fontWeight:700,color:C.text,fontFamily:C.syne,marginBottom:6}}>Tell us about your company</h2>
            <p style={{fontSize:13,color:C.muted,fontFamily:C.inst,marginBottom:22}}>The more detail you provide, the sharper your AI audit will be.</p>
            <div style={{marginBottom:16}}>
              <AuditLabel>Company Name</AuditLabel>
              <AInput value={form.companyName} onChange={v=>upd("companyName",v)} placeholder="e.g. Crescent Textile Mills Ltd."/>
            </div>
            <div style={{marginBottom:16}}>
              <AuditLabel>Industry Sector</AuditLabel>
              <ASelect options={INDUSTRIES_LIST} value={form.industry} onChange={v=>upd("industry",v)} placeholder="Select your industry"/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:16}}>
              <div>
                <AuditLabel>Company Size</AuditLabel>
                <ASelect options={SIZES_LIST} value={form.companySize} onChange={v=>upd("companySize",v)} placeholder="Size"/>
              </div>
              <div>
                <AuditLabel>City</AuditLabel>
                <ASelect options={CITIES_LIST} value={form.city} onChange={v=>upd("city",v)} placeholder="City"/>
              </div>
            </div>
            <div style={{marginBottom:24}}>
              <AuditLabel>Annual Revenue</AuditLabel>
              <ASelect options={REVENUES_LIST} value={form.revenue} onChange={v=>upd("revenue",v)} placeholder="Select revenue range"/>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end"}}>
              <Btn onClick={()=>canNext()&&setStep(1)} disabled={!canNext()}>Continue →</Btn>
            </div>
          </div>
        )}

        {/* STEP 1 — Challenges */}
        {step===1&&(
          <div style={{background:"rgba(255,255,255,0.025)",border:`1px solid ${C.border}`,borderRadius:16,padding:"28px"}} className="fi">
            <h2 style={{fontSize:20,fontWeight:700,color:C.text,fontFamily:C.syne,marginBottom:6}}>What's slowing you down?</h2>
            <p style={{fontSize:13,color:C.muted,fontFamily:C.inst,marginBottom:22}}>Select every challenge you face — pick at least 2.</p>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:16}}>
              {CHALLENGES_LIST.map(c=>(
                <Chip key={c.id} label={c.label} icon={c.icon} selected={form.challenges.includes(c.id)} onClick={()=>tog("challenges",c.id)}/>
              ))}
            </div>
            {form.challenges.length>0&&(
              <div style={{fontSize:12,color:C.accent,fontFamily:C.inst,marginBottom:16}}>{form.challenges.length} challenge{form.challenges.length>1?"s":""} selected</div>
            )}
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <Btn variant="ghost" onClick={()=>setStep(0)}>← Back</Btn>
              <Btn onClick={()=>canNext()&&setStep(2)} disabled={!canNext()}>Continue →</Btn>
            </div>
          </div>
        )}

        {/* STEP 2 — Tech & Data */}
        {step===2&&(
          <div style={{background:"rgba(255,255,255,0.025)",border:`1px solid ${C.border}`,borderRadius:16,padding:"28px"}} className="fi">
            <h2 style={{fontSize:20,fontWeight:700,color:C.text,fontFamily:C.syne,marginBottom:6}}>Technology & data landscape</h2>
            <p style={{fontSize:13,color:C.muted,fontFamily:C.inst,marginBottom:22}}>Honest answers help us recommend the right AI for where you are now.</p>
            <div style={{marginBottom:20}}>
              <AuditLabel>Current Technology Level</AuditLabel>
              <div style={{display:"flex",flexDirection:"column",gap:7}}>
                {TECH_OPTIONS.map(o=><Radio key={o.v} label={o.label} selected={form.techLevel===o.v} onClick={()=>upd("techLevel",o.v)}/>)}
              </div>
            </div>
            <div style={{marginBottom:20}}>
              <AuditLabel>Data Maturity Level</AuditLabel>
              <div style={{display:"flex",flexDirection:"column",gap:7}}>
                {DATA_OPTIONS.map(o=><Radio key={o.v} label={o.label} selected={form.dataMaturity===o.v} onClick={()=>upd("dataMaturity",o.v)}/>)}
              </div>
            </div>
            <div style={{marginBottom:24}}>
              <AuditLabel>Annual AI/Tech Budget</AuditLabel>
              <ASelect options={BUDGETS_LIST} value={form.budget} onChange={v=>upd("budget",v)} placeholder="Select budget range"/>
            </div>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <Btn variant="ghost" onClick={()=>setStep(1)}>← Back</Btn>
              <Btn onClick={()=>canNext()&&setStep(3)} disabled={!canNext()}>Continue →</Btn>
            </div>
          </div>
        )}

        {/* STEP 3 — Goals */}
        {step===3&&(
          <div style={{background:"rgba(255,255,255,0.025)",border:`1px solid ${C.border}`,borderRadius:16,padding:"28px"}} className="fi">
            <h2 style={{fontSize:20,fontWeight:700,color:C.text,fontFamily:C.syne,marginBottom:6}}>Your goals & timeline</h2>
            <p style={{fontSize:13,color:C.muted,fontFamily:C.inst,marginBottom:22}}>Your goals determine which AI initiatives we prioritise.</p>
            <div style={{marginBottom:20}}>
              <AuditLabel>Business Goals</AuditLabel>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {GOALS_LIST.map(g=><Chip key={g.id} label={g.label} icon={g.icon} selected={form.goals.includes(g.id)} onClick={()=>tog("goals",g.id)}/>)}
              </div>
            </div>
            <div style={{marginBottom:20}}>
              <AuditLabel>Implementation Urgency</AuditLabel>
              <div style={{display:"flex",flexDirection:"column",gap:7}}>
                {URGENCY_LIST.map(o=><Radio key={o.v} label={o.label} selected={form.urgency===o.v} onClick={()=>upd("urgency",o.v)}/>)}
              </div>
            </div>
            <div style={{marginBottom:24}}>
              <AuditLabel>Anything else? (Optional)</AuditLabel>
              <textarea value={form.additionalInfo} onChange={e=>upd("additionalInfo",e.target.value)}
                placeholder="Specific constraints or context..."
                style={{width:"100%",padding:"10px 14px",borderRadius:8,border:`1px solid ${C.border}`,
                  background:"rgba(255,255,255,0.04)",color:C.text,fontSize:13,fontFamily:C.inst,
                  resize:"vertical",minHeight:70,outline:"none"}}/>
            </div>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <Btn variant="ghost" onClick={()=>setStep(2)}>← Back</Btn>
              <Btn onClick={()=>{if(canNext())runAudit();}} disabled={!canNext()}>Run Deep AI Audit →</Btn>
            </div>
          </div>
        )}

        {/* STEP 4 — Running / Results */}
        {step===4&&(
          <div className="fi">
            {/* Scan progress */}
            {!result&&!error&&(
              <div style={{background:"rgba(255,255,255,0.025)",border:`1px solid ${C.border}`,borderRadius:16,padding:"28px"}}>
                <div style={{marginBottom:20}}>
                  {SCAN_STEPS.map((s,i)=>(
                    <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",
                      opacity:i<=scanIdx?1:0.15,transition:"opacity 0.4s"}}>
                      <div style={{width:20,height:20,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,
                        background:i<scanIdx?"rgba(38,193,110,0.15)":i===scanIdx?C.accentD:"rgba(255,255,255,0.04)",
                        border:`1px solid ${i<scanIdx?C.accentB:i===scanIdx?C.accentB:C.border}`,flexShrink:0}}>
                        {i<scanIdx?"✓":"·"}
                      </div>
                      <span style={{fontSize:12,color:i<scanIdx?C.accent:i===scanIdx?C.text:C.muted,fontFamily:C.inst}}>{s}</span>
                    </div>
                  ))}
                </div>
                <div style={{display:"flex",gap:4}}>
                  {[0,1,2,3,4].map(i=>(
                    <div key={i} style={{width:6,height:6,borderRadius:"50%",background:C.accent,
                      animation:`pulse 1.2s ease ${i*0.15}s infinite`}}/>
                  ))}
                </div>
              </div>
            )}

            {error&&(
              <div style={{color:C.red,fontSize:13,padding:20,border:`1px solid ${C.redD}`,borderRadius:12,background:C.redD}}>
                {error} <button onClick={()=>{setStep(3);setError(null);}} style={{marginLeft:10,color:C.accent,background:"none",border:"none",cursor:"pointer",fontFamily:C.inst}}>Try again</button>
              </div>
            )}

            {result&&(
              <div>
                {/* Score Card */}
                <div style={{display:"grid",gridTemplateColumns:"auto 1fr",gap:20,padding:"20px 24px",
                  borderRadius:14,background:C.accentD,border:`1px solid ${C.accentB}`,marginBottom:20,alignItems:"center"}}>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:52,fontWeight:800,color:scoreColor,fontFamily:C.syne,lineHeight:1}}>{result.overallScore}</div>
                    <div style={{fontSize:10,color:C.muted,fontFamily:C.inst,letterSpacing:"0.08em",textTransform:"uppercase",marginTop:4}}>AI Score</div>
                    <div style={{fontSize:12,color:scoreColor,fontFamily:C.syne,fontWeight:700,marginTop:4}}>{result.readinessLabel}</div>
                  </div>
                  <div style={{fontSize:13,color:C.sub,lineHeight:1.7,fontFamily:C.inst}}>{result.executiveSummary}</div>
                </div>

                {/* Competitive Alert */}
                <div style={{display:"flex",gap:10,padding:"12px 16px",borderRadius:10,background:C.orangeD,
                  border:"1px solid rgba(251,146,60,0.2)",marginBottom:20}}>
                  <span style={{fontSize:16,flexShrink:0}}>⚡</span>
                  <div style={{fontSize:13,color:"#FED7AA",fontFamily:C.inst,lineHeight:1.6}}>
                    <strong style={{color:C.orange}}>Market Alert: </strong>{result.competitiveAlert}
                  </div>
                </div>

                {/* 6 Dimensions */}
                <div style={{padding:"18px",borderRadius:12,background:C.surface,border:`1px solid ${C.border}`,marginBottom:20}}>
                  <div style={{fontSize:11,color:C.muted,fontWeight:700,letterSpacing:"0.09em",textTransform:"uppercase",fontFamily:C.syne,marginBottom:14}}>6-Dimension Analysis</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {result.dimensions?.map((d,i)=>{
                      const dc = d.score>=60?C.green:d.score>=40?C.accent:C.red;
                      return (
                        <div key={i} style={{padding:"10px 12px",borderRadius:8,background:"rgba(255,255,255,0.02)",border:`1px solid ${C.border}`}}>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                            <span style={{fontSize:12,fontWeight:600,color:C.text,fontFamily:C.inst}}>{d.label}</span>
                            <span style={{fontSize:12,fontWeight:700,color:dc,fontFamily:C.syne}}>{d.score}</span>
                          </div>
                          <div style={{height:3,borderRadius:2,background:"rgba(255,255,255,0.07)",marginBottom:6}}>
                            <div style={{height:"100%",borderRadius:2,background:dc,width:`${d.score}%`,transition:"width 1s ease"}}/>
                          </div>
                          <div style={{fontSize:11,color:C.muted,fontFamily:C.inst}}>{d.insight}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Quick Wins */}
                <div style={{marginBottom:20}}>
                  <div style={{fontSize:11,color:C.accent,fontWeight:700,letterSpacing:"0.09em",textTransform:"uppercase",fontFamily:C.syne,marginBottom:12}}>⚡ Top 3 Quick Wins — Highest ROI First</div>
                  {result.quickWins?.map((w,i)=>(
                    <div key={i} style={{marginBottom:10,borderRadius:10,overflow:"hidden",border:`1px solid ${C.border}`,background:"rgba(255,255,255,0.02)"}}>
                      <div style={{padding:"10px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",background:"rgba(255,255,255,0.02)",borderBottom:`1px solid ${C.border}`}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div style={{width:22,height:22,borderRadius:6,background:C.accentD,border:`1px solid ${C.accentB}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:C.accent,fontFamily:C.syne}}>{w.rank}</div>
                          <span style={{fontSize:13,fontWeight:600,color:C.text,fontFamily:C.inst}}>{w.title}</span>
                        </div>
                        <span style={{fontSize:10,padding:"2px 8px",borderRadius:4,background:w.effort==="Low"?C.greenD:C.accentD,color:w.effort==="Low"?C.green:C.accent,fontWeight:600,fontFamily:C.inst}}>{w.effort} effort</span>
                      </div>
                      <div style={{padding:"12px 16px"}}>
                        <div style={{fontSize:12,color:C.sub,fontFamily:C.inst,marginBottom:10}}>{w.solution}</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:12,padding:"8px 12px",borderRadius:8,background:"rgba(255,255,255,0.02)",border:`1px solid ${C.border}`}}>
                          <span style={{fontSize:12,color:C.green}}>📈 {w.roiEstimate}</span>
                          <span style={{fontSize:12,color:C.sub}}>⏱ {w.timeline}</span>
                          <span style={{fontSize:12,color:C.sub}}>💰 {w.cost}</span>
                        </div>
                        {w.pakistanNote&&(
                          <div style={{marginTop:8,padding:"7px 12px",borderRadius:6,background:C.blueD,border:`1px solid rgba(56,139,248,0.2)`}}>
                            <span style={{fontSize:11,color:C.blue,fontFamily:C.inst}}>🇵🇰 {w.pakistanNote}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* ROI */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20}}>
                  {result.estimatedROI&&[
                    ["Year 1 Impact",result.estimatedROI.year1,C.green],
                    ["Year 2 Savings",result.estimatedROI.year2,C.accent],
                    ["Payback Period",result.estimatedROI.paybackPeriod,C.blue],
                  ].map(([k,v,c])=>(
                    <div key={k} style={{padding:"14px",borderRadius:10,background:`${c}08`,border:`1px solid ${c}20`,textAlign:"center"}}>
                      <div style={{fontSize:13,fontWeight:700,color:c,fontFamily:C.syne,marginBottom:4}}>{v}</div>
                      <div style={{fontSize:10,color:C.muted,fontFamily:C.inst,textTransform:"uppercase",letterSpacing:"0.06em"}}>{k}</div>
                    </div>
                  ))}
                </div>

                {/* LEAD CAPTURE */}
                <LeadCapture form={form} result={result} onReset={()=>{setStep(0);setResult(null);setError(null);setScanIdx(0);setRunning(false);}} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── CLIENT DASHBOARD ──────────────────────────────────────── */
const ClientDashboard = () => {
  const [tab,setTab] = useState("projects");
  const [matching,setMatching] = useState(false);
  const [matched,setMatched] = useState(false);
  const [matchProgress,setMatchProgress] = useState(0);

  const runMatch = () => {
    setMatching(true);setMatchProgress(0);
    let p=0;
    const iv=setInterval(()=>{p+=7;setMatchProgress(Math.min(p,100));if(p>=100){clearInterval(iv);setMatching(false);setMatched(true);setTab("matches");}},120);
  };

  return (
    <div style={{padding:"28px 32px",maxWidth:960,margin:"0 auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:28}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <Avatar initials="CT" color={C.accent} size={40}/>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:C.text,fontFamily:C.syne}}>Crescent Textile Mills</div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <Badge label="VERIFIED BUSINESS" color={C.green}/>
              <span style={{fontSize:11,color:C.muted,fontFamily:C.inst}}>Faisalabad · Manufacturing</span>
            </div>
          </div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:11,color:C.muted,fontFamily:C.inst,marginBottom:4}}>AI Readiness Score</div>
          <div style={{fontSize:32,fontWeight:800,color:C.accent,fontFamily:C.syne,lineHeight:1}}>74</div>
          <div style={{fontSize:11,color:C.sub,fontFamily:C.inst}}>Ready for AI</div>
        </div>
      </div>
      <div style={{padding:"16px 20px",borderRadius:12,background:"linear-gradient(135deg,rgba(38,193,110,0.07),rgba(56,139,248,0.07))",
        border:`1px solid rgba(38,193,110,0.28)`,marginBottom:24,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:C.accent,fontFamily:C.syne,marginBottom:3}}>✅ AI Audit Complete — 3 Solutions Recommended</div>
          <div style={{fontSize:12,color:C.sub,fontFamily:C.inst}}>Top picks: Demand Forecasting · Defect Detection Vision · Urdu HR Assistant</div>
        </div>
        {!matched
          ? <Btn onClick={runMatch} style={{whiteSpace:"nowrap",marginLeft:16}}>{matching?"⟳ Matching...":"🤖 Find My Experts →"}</Btn>
          : <Badge label="EXPERTS MATCHED ✓" color={C.green}/>}
      </div>
      {matching&&(
        <div style={{marginBottom:20,padding:"14px 18px",borderRadius:10,background:C.blueD,border:`1px solid rgba(56,139,248,0.2)`}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
            <span style={{fontSize:12,color:C.blue,fontFamily:C.inst}}>AI matching engine running…</span>
            <span style={{fontSize:12,color:C.blue,fontFamily:C.syne,fontWeight:700}}>{matchProgress}%</span>
          </div>
          <ProgressBar pct={matchProgress} color={C.blue}/>
        </div>
      )}
      <div style={{display:"flex",gap:2,marginBottom:22,borderBottom:`1px solid ${C.border}`}}>
        {[{id:"projects",label:"My Projects",count:3},{id:"matches",label:"AI Matches",count:matched?3:0,highlight:matched},{id:"messages",label:"Messages",count:10}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{padding:"10px 16px",background:"transparent",border:"none",cursor:"pointer",
            borderBottom:`2px solid ${tab===t.id?C.accent:"transparent"}`,color:tab===t.id?C.accent:C.sub,
            fontSize:13,fontFamily:C.inst,fontWeight:tab===t.id?600:400,display:"flex",alignItems:"center",gap:6}}>
            {t.label}
            {t.count>0&&<span style={{fontSize:10,background:t.highlight?C.accentD:"rgba(255,255,255,0.06)",color:t.highlight?C.accent:C.muted,padding:"1px 6px",borderRadius:10,fontWeight:700}}>{t.count}</span>}
          </button>
        ))}
      </div>
      {tab==="projects"&&(
        <div className="fi">
          {CLIENT_PROJECTS.map(p=>(
            <div key={p.id} style={{padding:"18px 20px",borderRadius:12,background:C.surface,border:`1px solid ${C.border}`,marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                <div>
                  <div style={{fontSize:15,fontWeight:700,color:C.text,fontFamily:C.syne,marginBottom:4}}>{p.title}</div>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <StatusDot status={p.status}/>
                    <span style={{fontSize:11,color:C.muted,fontFamily:C.inst}}>Expert: {p.expert}</span>
                    <span style={{fontSize:11,color:C.muted,fontFamily:C.inst}}>Due: {p.due}</span>
                  </div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:14,fontWeight:700,color:C.accent,fontFamily:C.syne}}>{p.budget}</div>
                  <div style={{fontSize:11,color:C.muted,fontFamily:C.inst}}>{p.phase}</div>
                </div>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                <span style={{fontSize:11,color:C.muted,fontFamily:C.inst}}>Progress</span>
                <span style={{fontSize:11,color:p.progress===100?C.green:C.accent,fontFamily:C.syne,fontWeight:700}}>{p.progress}%</span>
              </div>
              <ProgressBar pct={p.progress} color={p.progress===100?C.green:C.accent}/>
              <div style={{display:"flex",gap:8,marginTop:12}}>
                <Btn small variant="ghost">View Details</Btn>
                {p.messages>0&&<Btn small variant="blue">💬 {p.messages} Messages</Btn>}
                {p.status==="Review"&&<Btn small>✓ Approve Delivery</Btn>}
              </div>
            </div>
          ))}
        </div>
      )}
      {tab==="matches"&&(
        <div className="fi">
          {!matched
            ? <div style={{textAlign:"center",padding:"40px 20px",color:C.muted,fontFamily:C.inst}}>Run AI matching to see your expert recommendations</div>
            : <div>
                <div style={{padding:"12px 16px",borderRadius:8,background:C.accentD,border:`1px solid ${C.accentB}`,marginBottom:18}}>
                  <span style={{fontSize:12,color:C.accent,fontFamily:C.inst}}>🤖 Claude matched 3 certified experts for your audit results.</span>
                </div>
                {EXPERTS.slice(0,3).map((e,i)=>(
                  <div key={e.id} style={{padding:"18px 20px",borderRadius:12,background:C.surface,border:`1px solid ${i===0?C.accentB:C.border}`,marginBottom:12,position:"relative"}}>
                    {i===0&&<div style={{position:"absolute",top:-1,right:16,background:C.accent,color:"#07090C",fontSize:10,fontWeight:800,padding:"3px 10px",borderRadius:"0 0 8px 8px",fontFamily:C.syne,letterSpacing:"0.06em"}}>BEST MATCH</div>}
                    <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
                      <Avatar initials={e.avatar} color={e.color} size={44}/>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                          <div>
                            <div style={{fontSize:15,fontWeight:700,color:C.text,fontFamily:C.syne}}>{e.name}</div>
                            <div style={{fontSize:12,color:C.sub,fontFamily:C.inst}}>{e.title} · {e.city}</div>
                          </div>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:14,fontWeight:700,color:C.accent,fontFamily:C.syne}}>{e.rate}</div>
                            <div style={{fontSize:11,color:C.sub,fontFamily:C.inst}}>⭐ {e.rating} · {e.jobs} projects</div>
                          </div>
                        </div>
                        <div style={{fontSize:12,color:C.sub,fontFamily:C.inst,marginBottom:10,lineHeight:1.5}}>{e.bio}</div>
                        <div style={{display:"flex",gap:8}}>
                          <Btn small>Hire {e.name.split(" ")[0]}</Btn>
                          <Btn small variant="ghost">View Profile</Btn>
                          <StatusDot status={e.availability}/>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>
      )}
      {tab==="messages"&&<div className="fi" style={{color:C.sub,fontFamily:C.inst,fontSize:13,padding:"20px 0"}}>💬 Messaging interface — connect with your active experts here.</div>}
    </div>
  );
};

/* ── EXPERT MARKETPLACE ────────────────────────────────────── */
const ExpertMarketplace = () => {
  const [filter,setFilter] = useState("All");
  const [selected,setSelected] = useState(null);
  const skills = ["All","NLP & LLM","Computer Vision","MLOps","Consulting","Analytics","Chatbots"];
  const filtered = filter==="All" ? EXPERTS : EXPERTS.filter(e=>e.skills.some(s=>s.toLowerCase().includes(filter.toLowerCase().split(" ")[0].toLowerCase())));

  if(selected) return (
    <div style={{padding:"28px 32px",maxWidth:700,margin:"0 auto"}} className="fi">
      <button onClick={()=>setSelected(null)} style={{background:"transparent",border:"none",cursor:"pointer",color:C.sub,fontSize:13,fontFamily:C.inst,marginBottom:20}}>← Back to Marketplace</button>
      <div style={{padding:"24px",borderRadius:14,background:C.surface,border:`1px solid ${C.border}`}}>
        <div style={{display:"flex",gap:16,alignItems:"flex-start",marginBottom:20}}>
          <Avatar initials={selected.avatar} color={selected.color} size={56}/>
          <div style={{flex:1}}>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <div>
                <div style={{fontSize:20,fontWeight:800,color:C.text,fontFamily:C.syne}}>{selected.name}</div>
                <div style={{fontSize:13,color:C.sub,fontFamily:C.inst,marginBottom:6}}>{selected.title}</div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <Badge label={selected.badge} color={selected.color}/>
                  {selected.verified&&<Badge label="CERTIFIED ✓" color={C.green}/>}
                  <StatusDot status={selected.availability}/>
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:22,fontWeight:800,color:C.accent,fontFamily:C.syne}}>{selected.rate}</div>
                <div style={{fontSize:12,color:C.sub,fontFamily:C.inst}}>⭐ {selected.rating} · {selected.jobs} completed</div>
              </div>
            </div>
          </div>
        </div>
        <div style={{fontSize:13,color:C.sub,fontFamily:C.inst,lineHeight:1.7,marginBottom:16}}>{selected.bio}</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:20}}>
          {selected.skills.map(s=><Tag key={s} label={s}/>)}
        </div>
        <Btn onClick={()=>alert("Project request submitted! AIpartners.pk will facilitate the contract.")}>Hire {selected.name.split(" ")[0]} →</Btn>
      </div>
    </div>
  );

  return (
    <div style={{padding:"28px 32px",maxWidth:960,margin:"0 auto"}}>
      <div style={{marginBottom:24}}>
        <h2 style={{fontSize:26,fontWeight:800,color:C.text,fontFamily:C.syne,marginBottom:6}}>Certified AI Experts</h2>
        <p style={{fontSize:13,color:C.sub,fontFamily:C.inst}}>All experts are vetted, certified and background-checked by AIpartners.pk.</p>
      </div>
      <div style={{display:"flex",gap:10,marginBottom:22}}>
        {[["89","Certified Experts",C.accent],["4.8","Avg Rating",C.blue],["134","Active Projects",C.purple],["98%","Client Satisfaction",C.green]].map(([v,l,c])=>(
          <div key={l} style={{flex:1,padding:"12px 14px",borderRadius:10,background:`${c}07`,border:`1px solid ${c}20`,textAlign:"center"}}>
            <div style={{fontSize:20,fontWeight:800,color:c,fontFamily:C.syne}}>{v}</div>
            <div style={{fontSize:10,color:C.muted,fontFamily:C.inst,textTransform:"uppercase",letterSpacing:"0.06em"}}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:20}}>
        {skills.map(s=>(
          <button key={s} onClick={()=>setFilter(s)} style={{padding:"7px 14px",borderRadius:6,
            border:`1px solid ${filter===s?C.accentB:C.border}`,background:filter===s?C.accentD:"transparent",
            color:filter===s?C.accent:C.sub,cursor:"pointer",fontSize:12,fontFamily:C.inst}}>{s}</button>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
        {filtered.map(e=>(
          <div key={e.id} style={{padding:"18px 20px",borderRadius:12,background:C.surface,border:`1px solid ${C.border}`,cursor:"pointer",transition:"all 0.15s"}}
            onMouseEnter={el=>{el.currentTarget.style.border=`1px solid ${e.color}35`;el.currentTarget.style.background=`${e.color}04`}}
            onMouseLeave={el=>{el.currentTarget.style.border=`1px solid ${C.border}`;el.currentTarget.style.background=C.surface}}
            onClick={()=>setSelected(e)}>
            <div style={{display:"flex",gap:12,alignItems:"flex-start",marginBottom:12}}>
              <Avatar initials={e.avatar} color={e.color}/>
              <div style={{flex:1}}>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <div>
                    <div style={{fontSize:14,fontWeight:700,color:C.text,fontFamily:C.syne}}>{e.name}</div>
                    <div style={{fontSize:11,color:C.sub,fontFamily:C.inst}}>{e.title}</div>
                  </div>
                  <div style={{fontSize:13,fontWeight:700,color:C.accent,fontFamily:C.syne}}>{e.rate}</div>
                </div>
              </div>
            </div>
            <div style={{fontSize:12,color:C.sub,fontFamily:C.inst,marginBottom:10,lineHeight:1.5,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{e.bio}</div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:10}}>
              {e.skills.slice(0,3).map(s=><Tag key={s} label={s}/>)}
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:10,borderTop:`1px solid ${C.border}`}}>
              <div style={{display:"flex",gap:6}}>
                <Badge label={e.badge} color={e.color}/>
                {e.verified&&<Badge label="CERTIFIED" color={C.green}/>}
              </div>
              <StatusDot status={e.availability}/>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── ADMIN PANEL ───────────────────────────────────────────── */
const AdminPanel = () => {
  const [tab,setTab] = useState("overview");
  const [approved,setApproved] = useState([]);
  const [rejected,setRejected] = useState([]);

  return (
    <div style={{padding:"28px 32px",maxWidth:960,margin:"0 auto"}}>
      <div style={{marginBottom:24,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div>
          <Badge label="ADMIN ACCESS" color={C.purple}/>
          <h2 style={{fontSize:26,fontWeight:800,color:C.text,fontFamily:C.syne,marginTop:8}}>Platform Control Centre</h2>
        </div>
        <div style={{fontSize:12,color:C.muted,fontFamily:C.inst,textAlign:"right"}}>
          <div>Last sync: 2 min ago</div>
          <div style={{color:C.green}}>● All systems operational</div>
        </div>
      </div>
      <div style={{display:"flex",gap:12,marginBottom:26,flexWrap:"wrap"}}>
        {ADMIN_STATS.map(s=><Stat key={s.label} {...s}/>)}
      </div>
      <div style={{display:"flex",gap:2,marginBottom:22,borderBottom:`1px solid ${C.border}`}}>
        {[{id:"overview",label:"Overview"},{id:"experts",label:"Expert Vetting",count:PENDING_EXPERTS.length},{id:"projects",label:"Active Projects",count:134}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{padding:"9px 15px",background:"transparent",border:"none",cursor:"pointer",
            borderBottom:`2px solid ${tab===t.id?C.purple:"transparent"}`,color:tab===t.id?C.purple:C.sub,
            fontSize:12,fontFamily:C.inst,fontWeight:tab===t.id?600:400,display:"flex",alignItems:"center",gap:5}}>
            {t.label}
            {t.count&&<span style={{fontSize:10,background:C.purpleD,color:C.purple,padding:"1px 5px",borderRadius:8,fontWeight:700}}>{t.count}</span>}
          </button>
        ))}
      </div>
      {tab==="overview"&&(
        <div className="fi" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <div style={{padding:"18px 20px",borderRadius:12,background:C.surface,border:`1px solid ${C.border}`}}>
            <SectionTitle>Recent Activity</SectionTitle>
            {[
              {icon:"🏢",text:"New business registered: Hassan Fabrics, Karachi",time:"12 min ago"},
              {icon:"👨‍💻",text:"Expert application: Mariam Khan (Data Scientist)",time:"1 hr ago"},
              {icon:"⚡",text:"Project started: Demand Forecasting @ Noon Foods",time:"3 hr ago"},
              {icon:"✅",text:"Project completed: Chatbot @ SadaPay — rated 5⭐",time:"5 hr ago"},
              {icon:"💰",text:"Commission received: PKR 85,000 from Tech Hub",time:"1 day ago"},
            ].map((a,i)=>(
              <div key={i} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:i<4?`1px solid ${C.border}`:"none"}}>
                <span style={{fontSize:14,flexShrink:0}}>{a.icon}</span>
                <div>
                  <div style={{fontSize:12,color:C.sub,fontFamily:C.inst,lineHeight:1.4}}>{a.text}</div>
                  <div style={{fontSize:10,color:C.muted,fontFamily:C.inst,marginTop:2}}>{a.time}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{padding:"18px 20px",borderRadius:12,background:C.surface,border:`1px solid ${C.border}`}}>
            <SectionTitle>Revenue This Month</SectionTitle>
            {[{label:"Platform Commissions",value:"PKR 2.8M",pct:67,c:C.accent},{label:"Expert Certifications",value:"PKR 750K",pct:18,c:C.blue},{label:"Enterprise Subs",value:"PKR 420K",pct:10,c:C.purple},{label:"Audit Pro Reports",value:"PKR 210K",pct:5,c:C.green}].map((r,i)=>(
              <div key={i} style={{marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{fontSize:12,color:C.sub,fontFamily:C.inst}}>{r.label}</span>
                  <span style={{fontSize:12,fontWeight:700,color:r.c,fontFamily:C.syne}}>{r.value}</span>
                </div>
                <ProgressBar pct={r.pct} color={r.c}/>
              </div>
            ))}
            <div style={{marginTop:16,padding:"10px 12px",borderRadius:8,background:C.accentD,border:`1px solid ${C.accentB}`,display:"flex",justifyContent:"space-between"}}>
              <span style={{fontSize:12,color:C.accent,fontFamily:C.inst,fontWeight:600}}>Total Revenue</span>
              <span style={{fontSize:14,fontWeight:800,color:C.accent,fontFamily:C.syne}}>PKR 4.18M</span>
            </div>
          </div>
        </div>
      )}
      {tab==="experts"&&(
        <div className="fi">
          <SectionTitle sub="These experts have applied and passed the technical test. Review & certify.">Pending Expert Certifications</SectionTitle>
          {PENDING_EXPERTS.map((e,i)=>{
            const isApproved=approved.includes(i),isRejected=rejected.includes(i);
            return (
              <div key={i} style={{padding:"16px 20px",borderRadius:12,background:C.surface,marginBottom:10,
                border:`1px solid ${isApproved?C.accentB:isRejected?C.redD:C.border}`,opacity:isRejected?0.5:1,transition:"all 0.3s"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    <Avatar initials={e.name.split(" ").map(n=>n[0]).join("")} color={C.blue}/>
                    <div>
                      <div style={{fontSize:14,fontWeight:700,color:C.text,fontFamily:C.syne}}>{e.name}</div>
                      <div style={{fontSize:12,color:C.sub,fontFamily:C.inst}}>{e.title} · {e.city} · Applied {e.applied}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:18,fontWeight:800,color:e.score>=85?C.green:C.accent,fontFamily:C.syne}}>{e.score}</div>
                      <div style={{fontSize:9,color:C.muted,fontFamily:C.inst,textTransform:"uppercase"}}>Tech Score</div>
                    </div>
                    {!isApproved&&!isRejected&&(
                      <div style={{display:"flex",gap:6}}>
                        <Btn small variant="danger" onClick={()=>setRejected(r=>[...r,i])}>Reject</Btn>
                        <Btn small variant="green" onClick={()=>setApproved(a=>[...a,i])}>Certify ✓</Btn>
                      </div>
                    )}
                    {isApproved&&<Badge label="CERTIFIED ✓" color={C.green}/>}
                    {isRejected&&<Badge label="REJECTED" color={C.red}/>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {tab==="projects"&&(
        <div className="fi">
          {[
            {client:"Crescent Textile",expert:"Bilal C.",project:"Demand Forecasting",budget:"$8,500",progress:65,status:"On Track"},
            {client:"Noon Foods",expert:"Ahsan M.",project:"Inventory AI",budget:"$6,200",progress:40,status:"On Track"},
            {client:"SadaPay",expert:"Fatima B.",project:"Urdu Chatbot",budget:"$4,200",progress:90,status:"In Review"},
            {client:"DHL Pakistan",expert:"Zara S.",project:"Route Optimization",budget:"$12,000",progress:25,status:"On Track"},
            {client:"Shaukat Khanum",expert:"Sana I.",project:"AI Triage System",budget:"$18,000",progress:55,status:"At Risk"},
          ].map((p,i)=>(
            <div key={i} style={{display:"grid",gridTemplateColumns:"2fr 2fr 1.5fr 2fr 1fr",gap:10,padding:"12px 16px",borderRadius:8,background:C.surface,border:`1px solid ${p.status==="At Risk"?C.redD:C.border}`,marginBottom:8,alignItems:"center"}}>
              <div><div style={{fontSize:12,fontWeight:600,color:C.text,fontFamily:C.inst}}>{p.client}</div><div style={{fontSize:11,color:C.muted}}>{p.project}</div></div>
              <div style={{fontSize:12,color:C.sub,fontFamily:C.inst}}>Expert: {p.expert}</div>
              <div style={{fontSize:13,fontWeight:700,color:C.accent,fontFamily:C.syne}}>{p.budget}</div>
              <div><div style={{fontSize:10,color:C.muted,marginBottom:3}}>{p.progress}%</div><ProgressBar pct={p.progress} color={p.status==="At Risk"?C.red:C.accent}/></div>
              <Badge label={p.status} color={p.status==="At Risk"?C.red:p.status==="In Review"?C.orange:C.green}/>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── ROOT APP — SINGLE EXPORT ──────────────────────────────── */
export default function App() {
  const [active,setActive] = useState("overview");

  const views = {
    overview: <PlatformOverview setActive={setActive}/>,
    audit:    <AuditTool onBack={()=>setActive("overview")}/>,
    client:   <ClientDashboard/>,
    expert:   <ExpertMarketplace/>,
    admin:    <AdminPanel/>,
  };

  return (
    <div style={{display:"flex",minHeight:"100vh",background:C.bg,fontFamily:C.inst,
      backgroundImage:`radial-gradient(ellipse at 0% 0%,rgba(38,193,110,0.04) 0%,transparent 50%),radial-gradient(ellipse at 100% 100%,rgba(56,139,248,0.04) 0%,transparent 50%)`}}>
      <style>{GS}</style>
      <Sidebar active={active} setActive={setActive}/>
      <main style={{flex:1,overflowY:"auto",minHeight:"100vh"}}>
        {views[active]}
        <div style={{textAlign:"center",padding:"16px",fontSize:10,color:C.muted,fontFamily:C.inst,
          letterSpacing:"0.05em",borderTop:`1px solid ${C.border}`,marginTop:8}}>
          © 2025 <span style={{background:C.grad,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",fontWeight:700}}>AIpartners.pk</span> · Powered by Claude AI · Data is never sold
        </div>
      </main>
    </div>
  );
}
